import React from 'react';
import {
  useGetReservationPartiesLookupQuery,
  useGetReservationWithAssociationsByIdQuery,
  useGetReservationsListQuery,
} from '../../Reservation/Service/extendedReservationsApi';
import {
  mapReservationToInvoiceLookup,
  type InvoiceReservationLookup,
} from './reservationsLookupMapper';
import { useGetBookingByIdQuery } from '../../Bookings/Service/bookingsApi';
import {
  mapReservationPartyLookupToInvoiceLookup,
  type InvoiceReservationPartyLookup,
  type InvoiceReservationPartyLookupRequest,
} from './reservationPartiesLookupMapper';

interface ReservationListArgs {
  pageNumber?: number;
  pageSize?: number;
  searchTerm?: string;
}

export const useGetInvoiceReservationsListQuery = (
  args: ReservationListArgs,
  options?: { skip?: boolean }
) => {
  const queryResult = useGetReservationsListQuery(args as any, options as any);

  const data = React.useMemo(
    () => ({
      ...(queryResult.data ?? {}),
      items: ((queryResult.data as any)?.items ?? [])
        .map((reservation: any) => mapReservationToInvoiceLookup(reservation))
        .filter(
          (
            reservation: InvoiceReservationLookup | undefined
          ): reservation is InvoiceReservationLookup => Boolean(reservation)
        ),
    }),
    [queryResult.data]
  );

  return {
    ...queryResult,
    data,
  };
};

export const useGetInvoiceReservationWithAssociationsByIdQuery = (
  reservationId: number,
  options?: { skip?: boolean }
) => {
  const queryResult = useGetReservationWithAssociationsByIdQuery(reservationId, options as any);

  const mappedReservation = React.useMemo(
    () => mapReservationToInvoiceLookup(queryResult.data as any),
    [queryResult.data]
  );

  const bookingId = mappedReservation?.booking?.id ?? mappedReservation?.bookingId;
  const shouldHydrateBookingFallback = Boolean(bookingId) &&
    (!mappedReservation?.dateFrom ||
      !mappedReservation?.dateTo ||
      !mappedReservation?.booking?.bookingDateFrom ||
      !mappedReservation?.booking?.bookingDateTo ||
      !mappedReservation?.booking?.offerName);
  const bookingResult = useGetBookingByIdQuery(bookingId!, {
    skip: !shouldHydrateBookingFallback,
  });

  const data = React.useMemo(() => {
    if (!mappedReservation) {
      return mappedReservation;
    }

    const booking = bookingResult.data;
    if (!booking) {
      return mappedReservation;
    }

    return {
      ...mappedReservation,
      offerName: mappedReservation.offerName || booking.offerName,
      dateFrom: mappedReservation.dateFrom || booking.bookingDateFrom,
      dateTo: mappedReservation.dateTo || booking.bookingDateTo,
      booking: {
        ...mappedReservation.booking,
        id: mappedReservation.booking?.id ?? booking.id,
        offerId: mappedReservation.booking?.offerId ?? booking.offerId,
        offerName: mappedReservation.booking?.offerName || booking.offerName,
        bookingDateFrom:
          mappedReservation.booking?.bookingDateFrom || booking.bookingDateFrom,
        bookingDateTo: mappedReservation.booking?.bookingDateTo || booking.bookingDateTo,
        price: mappedReservation.booking?.price ?? booking.price,
      },
    };
  }, [bookingResult.data, mappedReservation]);

  return {
    ...queryResult,
    data,
  };
};

export const useGetInvoiceReservationPartiesLookupQuery = (
  args: InvoiceReservationPartyLookupRequest,
  options?: { skip?: boolean }
) => {
  const queryResult = useGetReservationPartiesLookupQuery(args, options);

  const data = React.useMemo(
    () =>
      (queryResult.data ?? [])
        .map((party) => mapReservationPartyLookupToInvoiceLookup(party))
        .filter(
          (
            party: InvoiceReservationPartyLookup | undefined
          ): party is InvoiceReservationPartyLookup => Boolean(party)
        ),
    [queryResult.data]
  );

  return {
    ...queryResult,
    data,
  };
};
